import type { CSSProperties } from 'react';

import type {
  SidebarCalloutTitleLevel,
  ThemeTokens,
} from '@course-authoring/shared-types';

import { interactionCard } from './contentBlocks';
import { getTitleTypography } from './titleTypography';

export interface SidebarCalloutStyles {
  layout: CSSProperties;
  mainContent: CSSProperties;
  title: CSSProperties;
  body: CSSProperties;
  sidebar: CSSProperties;
  sidebarCentered: CSSProperties;
  calloutTitle: CSSProperties;
  calloutBody: CSSProperties;
}

const DESKTOP_LAYOUT_WIDTH = '80%';
const DESKTOP_MAIN_WIDTH = '62.5%';
const DESKTOP_SIDEBAR_WIDTH = '31.25%';
const DESKTOP_GAP_WIDTH = '6.25%';

export function createSidebarCalloutStyles(
  tokens: ThemeTokens,
  isMobile: boolean,
  titleLevel: SidebarCalloutTitleLevel = 'h2'
): SidebarCalloutStyles {
  const bodyFont = isMobile ? tokens.typography.mobile.body : tokens.typography.body;
  const titleFont = getTitleTypography(tokens, titleLevel, { isMobile });
  const contentGap = tokens.spacing.xl;
  const calloutPadding = `calc(${tokens.spacing["2xl"]} - 2px)`;

  return {
    layout: isMobile
      ? {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: contentGap,
          width: '100%',
          boxSizing: 'border-box',
        }
      : {
          display: 'grid',
          gridTemplateColumns: `${DESKTOP_MAIN_WIDTH} ${DESKTOP_SIDEBAR_WIDTH}`,
          columnGap: DESKTOP_GAP_WIDTH,
          alignItems: 'stretch',
          width: DESKTOP_LAYOUT_WIDTH,
          maxWidth: '100%',
          margin: '0 auto',
          boxSizing: 'border-box',
        },
    mainContent: {
      width: isMobile ? '80%' : '100%',
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      boxSizing: 'border-box',
    },
    title: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: titleFont.size,
      fontWeight: titleFont.weight,
      color: tokens.text.primary,
      lineHeight: titleFont.lineHeight,
      margin: 0,
    },
    body: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: bodyFont.size,
      fontWeight: bodyFont.weight,
      color: tokens.text.primary,
      lineHeight: bodyFont.lineHeight,
      marginTop: tokens.spacing.lg,
    },
    sidebar: {
      width: isMobile ? '80%' : '100%',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      boxSizing: 'border-box',
      padding: calloutPadding,
      ...interactionCard(tokens),
    },
    sidebarCentered: {
      justifyContent: 'center',
    },
    calloutTitle: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: bodyFont.size,
      fontWeight: bodyFont.weight,
      color: tokens.text.primary,
      lineHeight: bodyFont.lineHeight,
      margin: 0,
    },
    calloutBody: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: bodyFont.size,
      fontWeight: bodyFont.weight,
      color: tokens.text.primary,
      lineHeight: bodyFont.lineHeight,
      marginTop: tokens.spacing.md,
    },
  };
}
